/**
 * useImpersonationCountdown — client-side countdown driver for the
 * red AdminModeBar.
 *
 * Backend `expires_at` (ISO 8601) is the source of truth — set ONCE
 * at session-create and immutable thereafter. The client computes
 * remaining seconds from `expires_at - Date.now()` every second.
 *
 * Two one-shot side effects:
 *   - `onFiveMinuteWarning`: fires exactly once when remaining first
 *     drops to ≤ 5 min. Idempotent across refocus / tab change (the
 *     ref persists for the lifetime of the session).
 *   - `onExpire`: fires exactly once when remaining hits 0. Used by
 *     AdminModeBar to auto-trigger POST /admin/impersonation/exit.
 *
 * Both refs reset when `expiresAt` changes (new session = new
 * countdown lifecycle).
 *
 * Returns `remainingSeconds` (number ≥ 0) or `null` (no active
 * session).
 */
import { useEffect, useRef, useState } from "react";

export const FIVE_MINUTE_THRESHOLD_SECONDS = 5 * 60;

export function formatImpersonationRemaining(secs) {
  if (secs == null) return "";
  if (secs <= 0) return "expired";
  const mins = Math.floor(secs / 60);
  const s = secs % 60;
  if (secs >= FIVE_MINUTE_THRESHOLD_SECONDS + 1) {
    // > 5 min: whole-minute updates ("53m remaining")
    return `${mins}m remaining`;
  }
  if (secs >= 60) {
    // ≤ 5 min, ≥ 1 min: per-second mm:ss ("4m 32s")
    return `${mins}m ${String(s).padStart(2, "0")}s`;
  }
  // < 1 min: terse hint, paired with animate-pulse on the bar
  return "expires in <1m";
}

export default function useImpersonationCountdown({
  expiresAt,
  onFiveMinuteWarning,
  onExpire,
}) {
  const [remaining, setRemaining] = useState(null);
  const fiveMinFiredRef = useRef(false);
  const expiredFiredRef = useRef(false);

  useEffect(() => {
    // No active session — reset internal state and short-circuit.
    if (!expiresAt) {
      setRemaining(null);
      fiveMinFiredRef.current = false;
      expiredFiredRef.current = false;
      return undefined;
    }

    // Reset one-shot refs whenever a NEW session takes hold (different
    // `expiresAt` ISO string). Ensures back-to-back sessions each fire
    // their own 5-min warning + expire callbacks.
    fiveMinFiredRef.current = false;
    expiredFiredRef.current = false;

    const targetMs = new Date(expiresAt).getTime();

    const tick = () => {
      const deltaMs = targetMs - Date.now();
      const secs = Math.max(0, Math.floor(deltaMs / 1000));
      setRemaining(secs);

      if (
        secs <= FIVE_MINUTE_THRESHOLD_SECONDS &&
        !fiveMinFiredRef.current
      ) {
        fiveMinFiredRef.current = true;
        try {
          onFiveMinuteWarning && onFiveMinuteWarning();
        } catch {
          /* never let the callback throw stop the countdown */
        }
      }
      if (secs <= 0 && !expiredFiredRef.current) {
        expiredFiredRef.current = true;
        try {
          onExpire && onExpire();
        } catch {
          /* same — countdown is the hot path */
        }
      }
    };

    tick(); // synchronous first read so the bar paints immediately
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, onFiveMinuteWarning, onExpire]);

  return remaining;
}
